// „Prognoza miesiącami” on the Pulpit (owner 2026-10-29, decision
// docs/decisions/2026-10-30-przeciaganie-prognozy.md): the forecast rows of
// the ADMIN-only `forecastVsActual` block laid out per month, so that a ZK
// can be dragged to another month. Pure folding: the rows, their months and
// sources come from the backend (orderForecastTranches.js); a move only
// writes the ZK „Miesiąc prognozy” (`PUT /orders/:id` `forecastMonth`, the
// same field as the ZK card) and the page refetches the report.
//
// Only the part on no truck sits in the ZK forecast month:
//  - NO_TRUCK (or no source: a ZK without trucks) moves whole;
//  - MIXED (trucks delivering in that month + the part on no truck) moves
//    only its part on no truck, the trucks keep their delivery month;
//  - TRUCK never moves here: its month is the truck's expected delivery date.
import type { ForecastVsActualOrder } from '../incomeStatement/incomeStatementApi';

export type ForecastPlanSource = 'NO_TRUCK' | 'TRUCK' | 'MIXED';

export interface ForecastPlanCard {
  /** One card per ZK and month (a ZK may have tranches in several months). */
  key: string;
  orderId: string;
  displayOrderNumber: string;
  customerName: string;
  month: string;
  revenue: number;
  /** Known margin; with `costPending` the margin of the known cost (marked „*”). */
  margin: number | null;
  marginPercent: number | null;
  costPending: boolean;
  source: ForecastPlanSource;
  /** Share of the ZK remaining value in this month; null = the whole ZK. */
  share: number | null;
  /** The card can be dragged (its part on no truck changes month). */
  movable: boolean;
  /** Moved on screen, waiting for the saved report. */
  pending: boolean;
}

export interface ForecastPlanColumn {
  month: string;
  cards: ForecastPlanCard[];
  /** Sum of the cards' revenue = the forecast part of the month's bar (base scenario). */
  revenue: number;
}

/** Moves shown before the refetched report arrives: ZK id → target month. */
export type ForecastPlanMoves = Record<string, string>;

export function planSource(row: Pick<ForecastVsActualOrder, 'deliverySource'>): ForecastPlanSource {
  return row.deliverySource === 'TRUCK' || row.deliverySource === 'MIXED' ? row.deliverySource : 'NO_TRUCK';
}

function toCard(row: ForecastVsActualOrder): ForecastPlanCard | null {
  if (!row.month) return null;
  const source = planSource(row);
  return {
    key: `${row.id}:${row.month}`,
    orderId: row.id,
    displayOrderNumber: row.displayOrderNumber || row.orderNumber,
    customerName: row.customer?.name || '—',
    month: row.month,
    revenue: row.revenue,
    margin: row.margin,
    marginPercent: row.marginPercent,
    costPending: row.costPending,
    source,
    share: row.deliveryShare ?? null,
    movable: source !== 'TRUCK',
    pending: false,
  };
}

/**
 * Columns of the given months ('YYYY-MM', in order). A pending move puts the
 * NO_TRUCK card of that ZK into its target month on screen; a MIXED card
 * stays where it is (its split is the backend's) and is only marked pending.
 * Rows outside the months are left out (the report covers the page year).
 */
export function buildForecastPlanColumns(
  rows: ForecastVsActualOrder[],
  months: string[],
  moves: ForecastPlanMoves = {},
): ForecastPlanColumn[] {
  const columns = new Map<string, ForecastPlanColumn>(months.map((month) => [month, {
    month, cards: [], revenue: 0,
  }]));
  rows.forEach((row) => {
    const card = toCard(row);
    if (!card) return;
    const target = moves[card.orderId];
    if (target && card.movable) {
      card.pending = true;
      if (card.source === 'NO_TRUCK') card.month = target;
    }
    const column = columns.get(card.month);
    if (!column) return;
    column.cards.push(card);
    column.revenue += card.revenue;
  });
  return months.map((month) => {
    const column = columns.get(month)!;
    column.cards.sort((a, b) => b.revenue - a.revenue || a.displayOrderNumber.localeCompare(b.displayOrderNumber));
    column.revenue = Math.round(column.revenue * 100) / 100;
    return column;
  });
}

/** 'YYYY-MM' shifted by `offset` months. */
export function shiftMonth(month: string, offset: number): string {
  const index = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + offset;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

/** Months of the next year offered after the last month of the board (owner review 2026-10-30). */
export const NEXT_YEAR_TARGETS = 3;

export interface ForecastPlan {
  /** The current month and later months of the period: drop targets. */
  columns: ForecastPlanColumn[];
  /** Elapsed months of the period (oldest first); none when the period has not started. */
  pastMonths: string[];
  /**
   * ZK still waiting in an elapsed month („Zaległe”): one column, cards
   * oldest month first; null when no elapsed month holds a forecast. It is
   * not a drop target — its cards move forward through the menu or a drag.
   */
  overdue: ForecastPlanColumn | null;
}

/**
 * The board of the page period: the current and future months as columns,
 * the elapsed months folded into one „Zaległe” column (or one muted line).
 */
export function buildForecastPlan(
  rows: ForecastVsActualOrder[],
  periodMonths: string[],
  currentMonth: string,
  moves: ForecastPlanMoves = {},
): ForecastPlan {
  const all = buildForecastPlanColumns(rows, periodMonths, moves);
  const past = all.filter((column) => column.month < currentMonth);
  const columns = all.filter((column) => column.month >= currentMonth);
  const cards = past.flatMap((column) => column.cards);
  return {
    columns,
    pastMonths: past.map((column) => column.month),
    overdue: cards.length === 0 ? null : {
      month: past[0].month,
      cards,
      revenue: Math.round(past.reduce((sum, column) => sum + column.revenue, 0) * 100) / 100,
    },
  };
}

/**
 * Months a card can be moved to: the current month up to the end of the
 * page year (or of the current year, for a past page year) and the first
 * `NEXT_YEAR_TARGETS` months after it — never an elapsed month, never its own.
 */
export function moveTargets(card: Pick<ForecastPlanCard, 'month'>, currentMonth: string, pageYear: number): string[] {
  const lastYear = Math.max(pageYear, Number(currentMonth.slice(0, 4)));
  const last = shiftMonth(`${lastYear}-12`, NEXT_YEAR_TARGETS);
  const targets: string[] = [];
  for (let month = currentMonth; month <= last; month = shiftMonth(month, 1)) {
    if (month !== card.month) targets.push(month);
  }
  return targets;
}
